import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import navPrevious from "../../../assets/Images/TempleDetail/mobile/nav-previous.svg";
import navNext from "../../../assets/Images/TempleDetail/mobile/nav-next.svg";
import ctaArrow from "../../../assets/Images/TempleDetail/cta-arrow.svg";
import { T, FONT, FitPhoto } from "../shared";
import useLoopSlides from "../useLoopSlides";
import { MobileCard, MobileHeading } from "./shared";

// Figma "Mobile revamp" 739:17435 — Temple story: one chapter at a time (photo with its number,
// then label, title and text), swipe or use the arrows; counter and arrows sit under the slide.


// Arrow drawn inline so it can be white on the red button or grey on the white one.
const Chevron = ({ dir, color, size }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true" style={{ display: "block" }}>
    <path d={dir < 0 ? "M24 27L16 20L24 13" : "M16 27L24 20L16 13"} stroke={color} strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GAP = 16;

// Admin rich text keeps its words, bold, lists and links; the design sets everything else.
const htmlSx = {
  fontFamily: FONT,
  fontSize: "14px",
  lineHeight: 1.5,
  color: "#4A4542",
  wordBreak: "break-word",
  "& *": {
    fontFamily: `${FONT}, sans-serif !important`,
    fontSize: "inherit !important",
    lineHeight: "inherit !important",
    color: "inherit !important",
    backgroundColor: "transparent !important",
  },
  "& p": { m: 0 },
  "& p + p": { mt: "10px" },
  "& p:empty, & p:has(> br:only-child)": { display: "none" },
  "& strong, & b": { color: `${T.ink} !important`, fontWeight: 700 },
  "& ul, & ol": { pl: "20px", my: "6px" },
  "& a": { color: `${T.red} !important` },
};

const MobileStory = ({ story, onDonate }) => {
  const slides = story?.slides || [];
  // Seamless loop: after the last chapter the first slides in from the right
  const { pos, index, animate, move, settle, copies } = useLoopSlides(slides.length);
  const touchX = useRef(null);
  if (!slides.length) return null;

  const nav = (dir) => {
    const disabled = slides.length < 2;
    // The story loops, so "next" is always the way forward (red)
    const filled = dir > 0;
    return (
      <Box
        component="button"
        type="button"
        aria-label={dir < 0 ? "Previous chapter" : "Next chapter"}
        disabled={disabled}
        onClick={() => move(dir)}
        sx={{
          width: "40px",
          height: "40px",
          p: 0,
          border: filled ? "none" : "1px solid #EFE4D3",
          boxSizing: "border-box",
          borderRadius: "20px",
          background: filled ? T.red : "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.2s, opacity 0.2s",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.4 : 1,
          "&:focus-visible": { outline: `2px solid ${T.ink}`, outlineOffset: "2px" },
        }}
      >
        <Chevron dir={dir} color={filled ? "#FFFFFF" : "#6B6B6B"} size={40} />
      </Box>
    );
  };

  return (
    <MobileCard id="story" sx={{ gap: "20px" }}>
      <MobileHeading eyebrow="The story" hindi="॥ कथा ॥" title={story.title} />

      <Box
        sx={{ position: "relative", overflow: "hidden", borderRadius: "18px" }}
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) > 40) move(dx < 0 ? 1 : -1);
          touchX.current = null;
        }}
      >
        <Box
          sx={{ display: "flex", gap: `${GAP}px`, alignItems: "stretch", transform: `translateX(calc(-${pos} * (100% + ${GAP}px)))`, transition: animate ? "transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1)" : "none" }}
          onTransitionEnd={(e) => e.target === e.currentTarget && e.propertyName === "transform" && settle()}
        >
          {Array.from({ length: copies }, (_, c) => slides.map((slide, i) => (
            <Box
              key={`${c}-${i}`}
              aria-hidden={c * slides.length + i !== pos}
              sx={{ flex: "0 0 100%", minWidth: 0, display: "flex", flexDirection: "column", border: "0.5px solid rgba(201,149,43,0.3)", borderRadius: "18px", overflow: "hidden", background: "#FFFFFF", boxSizing: "border-box" }}
            >
              <Box sx={{ position: "relative", height: "238px", flexShrink: 0, background: T.maroon }}>
                <FitPhoto src={slide.image} />
                <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(36,5,8,0) 60%, rgba(36,5,8,0.7) 100%)" }} />
                <Typography sx={{ position: "absolute", left: "18px", bottom: "8px", fontFamily: FONT, fontWeight: 700, fontSize: "46px", lineHeight: "normal", color: "#FFFFFF" }}>
                  {String(i + 1).padStart(2, "0")}
                </Typography>
              </Box>
              <Box sx={{ p: "18px", display: "flex", flexDirection: "column", gap: "12px" }}>
                {slide.tag && <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "9px", lineHeight: 1.5, letterSpacing: "0.9px", textTransform: "uppercase", color: T.red }}>{slide.tag}</Typography>}
                {slide.title && (
                  <Typography component="h3" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "23px", lineHeight: 1.5, color: T.ink }}>{slide.title}</Typography>
                )}
                {slide.html ? (
                  <Box sx={htmlSx} dangerouslySetInnerHTML={{ __html: slide.html }} />
                ) : (
                  <Typography sx={{ fontFamily: FONT, fontSize: "14px", lineHeight: 1.5, color: "#4A4542" }}>{slide.body}</Typography>
                )}
                {slide.cta && (
                  <Box
                    component="button"
                    type="button"
                    tabIndex={c * slides.length + i === pos ? 0 : -1}
                    onClick={() => onDonate?.()}
                    sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: "8px", px: "18px", py: "11px", border: "none", borderRadius: "100px", background: T.red, color: "#FFFFFF", fontFamily: FONT, fontWeight: 700, fontSize: "13px", cursor: "pointer" }}
                  >
                    {slide.cta}
                    <Box component="img" src={ctaArrow} alt="" sx={{ width: "14px", height: "14px" }} />
                  </Box>
                )}
              </Box>
            </Box>
          )))}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box aria-live="polite" sx={{ display: "flex", gap: "3px", alignItems: "baseline" }}>
          <Typography component="span" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "20px", lineHeight: "normal", color: T.red }}>{String(index + 1).padStart(2, "0")}</Typography>
          <Typography component="span" sx={{ fontFamily: FONT, fontSize: "13px", lineHeight: "normal", color: T.muted }}>/ {String(slides.length).padStart(2, "0")}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "10px" }}>
          {nav(-1)}
          {nav(1)}
        </Box>
      </Box>
    </MobileCard>
  );
};

export default MobileStory;
